import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pdf } from '@react-pdf/renderer';
import { Button, Image, Text } from '@msc-b2c/shared-components';
import {
  addDaysAtSea,
  AppDispatch,
  fetchContentFragmentsFromCms,
  getActiveSession,
  myAreaCabinDetails,
  replacePlaceholders,
  RootState,
  setShipCF,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  CabinCategoryCfmModel,
  DownloadPDFByDayLabelsProps,
  DownloadPDFFullCruiseLabelsProps,
  DownloadPDFTabCardProps,
  DownloadPDFTabType,
  DownloadPDFWrapperProps,
  GetShipsResponse,
  IGetShipsResponse,
  PdfType,
} from '@msc-b2c/interfaces/types';

import { PDFDocument } from './pdf-templates/PDFDocument';

import styles from './downloadpdf.module.scss';
import { IFetchShipsFromFragmentsURLParams, MyMscExcursion } from '@msc-b2c/interfaces/interfaces';
import { getCabinObjectOfTheLoggedUser } from '../../utils/myAreaUtils';

export function DownloadPDF({
  id = '',
  pdfDownloadSectionTitle = '',
  byDayTabTitle = '',
  fullCruiseTabTitle = '',
  byDayButtonLabel = '',
  byDayButtonIcon = '',
  fullCruiseButtonLabel = '',
  fullCruiseButtonIcon = '',
  byDayImage = '',
  byDayImageReference = '',
  byDayImageAltText = '',
  byDayDescription = '',
  fullCruiseImage = '',
  fullCruiseImageReference = '',
  fullCruiseImageAltText = '',
  fullCruiseDescription = '',
  byDayHeaderImgReference = '',
  byDayKeyToSymbolsImgReference = '',
  byDayKeyToSymbolsLabel = '',
  byDayOurExcursionsLabel = '',
  byDayCruiseDestinationLabel = '',
  byDayCruiseShipLabel = '',
  byDayCodeLabel = '',
  byDayExcursionDurationLabel = '',
  byDayPriceTypeLabel = '',
  byDayFirstPriceLabel = '',
  byDaySecondPriceLabel = '',
  byDayMinimumNoOfParticipantsRequiredText = '',
  byDayTourLanguageLabel = '',
  byDayCategoryDescription = '',
  byDayCopyrightFooter = '',
  byDayGeneratedLabelDateFooter = '',
  fullCruiseHeaderImgReference = '',
  fullCruiseKeyToSymbolsImgReference = '',
  fullCruiseKeyToSymbolsLabel = '',
  fullCruiseOurExcursionsLabel = '',
  fullCruiseDayFieldLabel = '',
  fullCruiseNightFieldLabel = '',
  fullCruiseCruiseShipLabel = '',
  fullCruiseCodeLabel = '',
  fullCruiseExcursionDurationLabel = '',
  fullCruisePriceTypeLabel = '',
  fullCruiseFirstPriceLabel = '',
  fullCruiseSecondPriceLabel = '',
  fullCruiseMinimumNoOfParticipantsRequiredText = '',
  fullCruiseTourLanguageLabel = '',
  fullCruiseCategoryDescription = '',
  fullCruiseItinerariesLabel = '',
  fullCruiseDepartureFromLabel = '',
  fullCruiseDepartureDateLabel = '',
  fullCruiseCruiseLabel = '',
  fullCruiseCopyrightFooter = '',
  fullCruiseGeneratedLabelDateFooter = '',
  excursionActivityCategories = [],
  dataComponentName,
}: Readonly<DownloadPDFWrapperProps>): ReactNode {
  const dispatch = useDispatch<AppDispatch>();
  const propertiesFromPlanMyCruiseContainer = useSelector(
    (state: RootState) => state.myAreaCommonConfig.prePaidCardProps
  );
  const excursions = useSelector((state: RootState) => state.myCruise.excursions);
  const { ship } = useSelector((state: RootState) => state.bookingContentFragment);
  const selectedDayDate = useSelector((state: RootState) => state.myCruise.selectedDayFilter?.date);
  const selectedDayPortCode = useSelector(
    (state: RootState) => state.myCruise.selectedDayFilter?.portCode
  );
  const selectedDayIsEmbarkation = useSelector(
    (state: RootState) => state.myCruise.selectedDayFilter?.isEmbarkationDay
  );
  const selectedDayIsDisembarkation = useSelector(
    (state: RootState) => state.myCruise.selectedDayFilter?.isDisembarkationDay
  );
  const isAllPortsSelected = selectedDayDate === 'allPorts';
  const isDayAtSeaSelected = selectedDayPortCode === 'SEA';

  const normalizeDate = (value?: string) => (value ? value.slice(0, 10) : '');

  const filterExcursionsBySelectedDay = (
    excursionList: MyMscExcursion[] | undefined
  ): MyMscExcursion[] | undefined => {
    if (!selectedDayDate || selectedDayDate === 'allPorts' || !excursionList) {
      return excursionList;
    }

    const disableEmbarkDisembark =
      propertiesFromPlanMyCruiseContainer.disableEmbarkAndDisembarkExcursions;

    if (selectedDayIsEmbarkation) {
      return excursionList.filter(
        (exc) =>
          (exc.isPackage && Number(exc.inventory?.numberAvailable) > 0) ||
          (exc.code.endsWith('E') &&
            exc.startDate === selectedDayDate &&
            exc.portCode === selectedDayPortCode &&
            !disableEmbarkDisembark)
      );
    }

    if (!disableEmbarkDisembark && selectedDayIsDisembarkation) {
      return excursionList.filter(
        (exc) =>
          exc.code.endsWith('D') &&
          exc.startDate === selectedDayDate &&
          exc.portCode === selectedDayPortCode
      );
    }

    if (isDayAtSeaSelected) {
      return excursionList.filter(
        (exc) =>
          exc.startDate === selectedDayDate &&
          exc.portCode === 'ATC' &&
          !exc.code.endsWith('D') &&
          !exc.code.endsWith('E')
      );
    }

    return excursionList.filter(
      (exc) =>
        exc.startDate === selectedDayDate &&
        exc.portCode === selectedDayPortCode &&
        !exc.code.endsWith('D') &&
        !exc.code.endsWith('E')
    );
  };

  let filteredExcursions: MyMscExcursion[] | undefined = excursions?.excursions?.filter(
    (exc) => !!exc.activityDetails && (!!exc?.activityGroup?.description || !!exc.isPackage)
  );

  filteredExcursions = filteredExcursions?.filter(
    (exc: MyMscExcursion) => Number(exc.inventory?.numberAvailable) > 0
  );

  const baseFilteredExcursions = filteredExcursions;

  const availableExcursions = excursions?.excursions?.filter(
    (exc: MyMscExcursion) => Number(exc.inventory?.numberAvailable) > 0
  );
  const [shipData, setShipData] = useState<GetShipsResponse>();
  const itinerary = useSelector((state: RootState) => state.myCruise.itinerary);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [activeTab, setActiveTab] = useState<DownloadPDFTabType>(DownloadPDFTabType.ByDay);
  const daysWithSea = itinerary ? addDaysAtSea(itinerary, '').days : [];

  const isFullCruiseTab = activeTab === DownloadPDFTabType.FullCruise;
  const selectedPdfType = isFullCruiseTab ? PdfType.FullCruiseItinerary : PdfType.DayPort;
  const embarkationDate = normalizeDate(itinerary?.days?.[0]?.date);
  const disembarkationDate = normalizeDate(itinerary?.days?.[itinerary.days.length - 1]?.date);

  const applyAllPortsExcursionsLogic = (excursions: MyMscExcursion[] | undefined) =>
    excursions?.filter((exc: MyMscExcursion) => {
      const excursionDate = normalizeDate(exc.startDate);
      const isEmbarkationExcursionDate = !!embarkationDate && excursionDate === embarkationDate;
      const isDisembarkationExcursionDate =
        !!disembarkationDate && excursionDate === disembarkationDate;
      const isEmbarkExcursion = exc.code.endsWith('E') || exc.isPackage;
      const isDisembarkExcursion = exc.code.endsWith('D');

      return (
        (!isEmbarkationExcursionDate &&
          !isDisembarkationExcursionDate &&
          !exc.code.endsWith('E') &&
          !exc.code.endsWith('D')) ||
        (isDisembarkationExcursionDate && isDisembarkExcursion) ||
        (isEmbarkationExcursionDate && isEmbarkExcursion)
      );
    });

  const fullCruiseExcursions = applyAllPortsExcursionsLogic(baseFilteredExcursions);

  const activeCard: DownloadPDFTabCardProps = isFullCruiseTab
    ? {
        image: fullCruiseImage || fullCruiseImageReference,
        imageAltText: fullCruiseImageAltText,
        description: fullCruiseDescription,
        buttonLabel: fullCruiseButtonLabel,
        buttonIcon: fullCruiseButtonIcon,
      }
    : {
        image: byDayImage || byDayImageReference,
        imageAltText: byDayImageAltText,
        description: byDayDescription,
        buttonLabel: byDayButtonLabel,
        buttonIcon: byDayButtonIcon,
      };

  const byDayPdfLabels: DownloadPDFByDayLabelsProps = {
    headerImgReference: byDayHeaderImgReference,
    keyToSymbolsImgReference: byDayKeyToSymbolsImgReference,
    keyToSymbolsLabel: byDayKeyToSymbolsLabel,
    ourExcursionsLabel: byDayOurExcursionsLabel,
    cruiseDestinationLabel: byDayCruiseDestinationLabel,
    cruiseShipLabel: byDayCruiseShipLabel,
    codeLabel: byDayCodeLabel,
    excursionDurationLabel: byDayExcursionDurationLabel,
    priceTypeLabel: byDayPriceTypeLabel,
    firstPriceLabel: byDayFirstPriceLabel,
    secondPriceLabel: byDaySecondPriceLabel,
    minimumNoOfParticipantsRequiredText: byDayMinimumNoOfParticipantsRequiredText,
    tourLanguageLabel: byDayTourLanguageLabel,
    categoryDescription: byDayCategoryDescription,
    copyrightFooter: byDayCopyrightFooter,
    generatedLabelDateFooter: byDayGeneratedLabelDateFooter,
  };

  const fullCruisePdfLabels: DownloadPDFFullCruiseLabelsProps = {
    headerImgReference: fullCruiseHeaderImgReference,
    keyToSymbolsImgReference: fullCruiseKeyToSymbolsImgReference,
    keyToSymbolsLabel: fullCruiseKeyToSymbolsLabel,
    ourExcursionsLabel: fullCruiseOurExcursionsLabel,
    dayFieldLabel: fullCruiseDayFieldLabel,
    nightFieldLabel: fullCruiseNightFieldLabel,
    cruiseShipLabel: fullCruiseCruiseShipLabel,
    codeLabel: fullCruiseCodeLabel,
    excursionDurationLabel: fullCruiseExcursionDurationLabel,
    priceTypeLabel: fullCruisePriceTypeLabel,
    firstPriceLabel: fullCruiseFirstPriceLabel,
    secondPriceLabel: fullCruiseSecondPriceLabel,
    minimumNoOfParticipantsRequiredText: fullCruiseMinimumNoOfParticipantsRequiredText,
    tourLanguageLabel: fullCruiseTourLanguageLabel,
    categoryDescription: fullCruiseCategoryDescription,
    itinerariesLabel: fullCruiseItinerariesLabel,
    departureFromLabel: fullCruiseDepartureFromLabel,
    departureDateLabel: fullCruiseDepartureDateLabel,
    cruiseLabel: fullCruiseCruiseLabel,
    copyrightFooter: fullCruiseCopyrightFooter,
    generatedLabelDateFooter: fullCruiseGeneratedLabelDateFooter,
  };

  const activePdfLabels = isFullCruiseTab ? fullCruisePdfLabels : byDayPdfLabels;

  const selectedDayData =
    selectedDayDate && selectedDayDate !== 'allPorts'
      ? daysWithSea.find(
          (day) => day.date === selectedDayDate && day.port.code === selectedDayPortCode
        )
      : undefined;

  const resolvedByDayTabTitle = replacePlaceholders(byDayTabTitle, {
    dayIndex: selectedDayData ? String(daysWithSea.indexOf(selectedDayData) + 1) : '',
    portName: selectedDayData?.port?.name ?? '',
  });

  if (selectedDayDate && selectedDayDate !== 'allPorts' && selectedPdfType === PdfType.DayPort) {
    filteredExcursions = filterExcursionsBySelectedDay(filteredExcursions);
  }

  const shouldApplyAllPortsExcursionsLogic =
    selectedPdfType === PdfType.FullCruiseItinerary || selectedDayDate === 'allPorts';

  if (shouldApplyAllPortsExcursionsLogic) {
    filteredExcursions = applyAllPortsExcursionsLogic(filteredExcursions);
  }

  const selectedDayExcursions =
    selectedDayDate && selectedDayDate !== 'allPorts'
      ? filterExcursionsBySelectedDay(availableExcursions)
      : undefined;

  const hasSelectedDayExcursions =
    !isAllPortsSelected &&
    !isDayAtSeaSelected &&
    !!selectedDayExcursions &&
    selectedDayExcursions.length > 0;

  useEffect(() => {
    if (isAllPortsSelected) {
      setActiveTab(DownloadPDFTabType.FullCruise);
    } else if (hasSelectedDayExcursions) {
      setActiveTab(DownloadPDFTabType.ByDay);
    } else {
      setActiveTab(DownloadPDFTabType.FullCruise);
    }
  }, [isAllPortsSelected, hasSelectedDayExcursions, selectedDayDate, selectedDayPortCode]);

  const { bookingNumber = '', cruiseId } = getActiveSession();

  const { data: bookingData } = useGetBookingQuery(bookingNumber || skipToken, {
    selectFromResult: ({ data }) => ({ data }),
  });

  const fetchShipDataFromFragments = useCallback(() => {
    const bookingShipCode = bookingData?.cabins[0]?.cruise?.ship?.code.slice(0, 2) ?? '';

    const updateCabinDetails = (selectedShip?: GetShipsResponse) => {
      // extract the cabin details of the logged user
      const cabinOfTheLoggedUser = getCabinObjectOfTheLoggedUser(bookingData);
      // find cabinOfTheLoggedUser?.categoryCode inside the cabinCode of
      // interiorCategoryCf or oceanViewCategoryCf or balconyCategoryCf or suiteCategoryCf or yachtClubMacrocategoryCf
      const categoryCode = cabinOfTheLoggedUser?.categoryCode;
      const cabinCategoryInterior = selectedShip?.interiorCategoryCf?.find(
        (item) => item.cabinCode === categoryCode
      );
      const cabinCategoryOceanView = selectedShip?.oceanViewCategoryCf?.find(
        (item) => item.cabinCode === categoryCode
      );
      const cabinCategoryBalcony = selectedShip?.balconyCategoryCf?.find(
        (item) => item.cabinCode === categoryCode
      );
      const cabinCategorySuite = selectedShip?.suiteCategoryCf?.find(
        (item) => item.cabinCode === categoryCode
      );
      const cabinCategoryYachtClub = selectedShip?.yachtClubCategoryCf?.find(
        (item) => item.cabinCode === categoryCode
      );

      const cabinCategory: CabinCategoryCfmModel | undefined =
        cabinCategoryInterior ??
        cabinCategoryOceanView ??
        cabinCategoryBalcony ??
        cabinCategorySuite ??
        cabinCategoryYachtClub ??
        undefined;

      // set the cabinCategory
      if (cabinCategory) {
        dispatch(myAreaCabinDetails(cabinCategory));
      }
    };

    const cachedShip = bookingShipCode === ship?.shipCode ? ship : undefined;

    if (cachedShip) {
      setShipData(cachedShip);
      updateCabinDetails(cachedShip);
      return;
    }

    fetchContentFragmentsFromCms<IGetShipsResponse, IFetchShipsFromFragmentsURLParams>('getShips', {
      shipCode: bookingShipCode,
    }).then((result: IGetShipsResponse) => {
      const selectedShip = result?.data?.shipCfmList?.items?.[0];
      setShipData(selectedShip);

      if (selectedShip) {
        dispatch(setShipCF(selectedShip));
      }

      updateCabinDetails(selectedShip);
    });
  }, [bookingData, dispatch, ship]);

  useEffect(() => {
    if (bookingData) fetchShipDataFromFragments();
  }, [bookingData]);

  const geoDestination =
    bookingData?.cabins?.find((cabin) => cabin.cruise.id === cruiseId)?.cruise?.demographicArea ??
    '';

  const fileName =
    selectedDayDate &&
    selectedDayDate !== 'allPorts' &&
    selectedPdfType !== PdfType.FullCruiseItinerary
      ? `excursions-date-${selectedDayDate}.pdf`
      : `fullCruiseItinerary-excursions.pdf`;

  const handleDownloadPdf = async () => {
    if (!filteredExcursions || filteredExcursions.length === 0) {
      return;
    }

    setIsGeneratingPdf(true);
    try {
      const pdfDocument = (
        <PDFDocument
          headerImgReference={activePdfLabels.headerImgReference}
          keyToSymbolsImgReference={activePdfLabels.keyToSymbolsImgReference}
          cruiseShipLabel={activePdfLabels.cruiseShipLabel}
          excursionDurationLabel={activePdfLabels.excursionDurationLabel}
          priceTypeLabel={activePdfLabels.priceTypeLabel}
          firstPriceLabel={activePdfLabels.firstPriceLabel}
          secondPriceLabel={activePdfLabels.secondPriceLabel}
          minimumNoOfParticipantsRequiredText={activePdfLabels.minimumNoOfParticipantsRequiredText}
          tourLanguageLabel={activePdfLabels.tourLanguageLabel}
          excursionActivityCategories={excursionActivityCategories
            .filter(
              (category): category is Required<typeof category> =>
                !!category.categoryCode && !!category.categoryColor && !!category.categoryTitle
            )
            .map((category) => ({
              categoryCode: category.categoryCode,
              categoryColor: category.categoryColor,
              categoryTitle: category.categoryTitle,
              categoryDescription: category.categoryDescription,
            }))}
          itinerary={itinerary}
          excursions={filteredExcursions}
          selectedDay={selectedDayDate}
          geographicalDestination={geoDestination}
          shipData={shipData}
          categoryDescription={activePdfLabels.categoryDescription}
          codeLabel={activePdfLabels.codeLabel}
          pdfType={selectedPdfType}
          dataComponentName={dataComponentName}
          ourExcursionsLabel={activePdfLabels.ourExcursionsLabel}
          keyToSymbolsLabel={activePdfLabels.keyToSymbolsLabel}
          copyrightFooter={activePdfLabels.copyrightFooter}
          generatedLabelDateFooter={activePdfLabels.generatedLabelDateFooter}
          {...(isFullCruiseTab
            ? {
                cruiseLabel: fullCruisePdfLabels.cruiseLabel,
                itinerariesLabel: fullCruisePdfLabels.itinerariesLabel,
                departureFromLabel: fullCruisePdfLabels.departureFromLabel,
                departureDateLabel: fullCruisePdfLabels.departureDateLabel,
                dayFieldLabel: fullCruisePdfLabels.dayFieldLabel,
                nightFieldLabel: fullCruisePdfLabels.nightFieldLabel,
              }
            : { cruiseDestinationLabel: byDayPdfLabels.cruiseDestinationLabel })}
        />
      );

      const asPdf = pdf(pdfDocument);
      const pdfBlob = await asPdf.toBlob();
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    !isDayAtSeaSelected &&
    fullCruiseExcursions &&
    fullCruiseExcursions.length > 0 && (
      <div
        id={id}
        className={styles['cmp-download-pdf']}
        data-component-name={dataComponentName}
      >
        <div className={styles['cmp-download-pdf__header']}>
          {pdfDownloadSectionTitle && (
            <Text
              text={pdfDownloadSectionTitle}
              tagHtml="p"
              styleOption={styles['cmp-download-pdf__section-title']}
            />
          )}
          <div className={styles['cmp-download-pdf__tabs']}>
            {hasSelectedDayExcursions && (
              <Button
                id="download-pdf-by-day-tab"
                buttonType="button"
                text={resolvedByDayTabTitle}
                onClick={() => setActiveTab(DownloadPDFTabType.ByDay)}
                styleOption={`${styles['cmp-download-pdf__tab']} ${
                  activeTab === DownloadPDFTabType.ByDay
                    ? styles['cmp-download-pdf__tab--active']
                    : ''
                }`}
              />
            )}
            <Button
              id="download-pdf-full-cruise-tab"
              buttonType="button"
              text={fullCruiseTabTitle}
              onClick={() => setActiveTab(DownloadPDFTabType.FullCruise)}
              styleOption={`${styles['cmp-download-pdf__tab']} ${
                activeTab === DownloadPDFTabType.FullCruise
                  ? styles['cmp-download-pdf__tab--active']
                  : ''
              }`}
            />
          </div>
        </div>
        <div className={styles['cmp-download-pdf__card']}>
          <div className={styles['cmp-download-pdf__card-main']}>
            {activeCard.image && (
              <Image
                id="excursion-image"
                src={activeCard.image}
                alt={activeCard.imageAltText}
                styleOption={styles['cmp-download-pdf__image']}
              />
            )}
            <Text
              text={activeCard.description}
              tagHtml="p"
              styleOption={styles['cmp-download-pdf__description']}
            />
            <Button
              id="download-pdf"
              buttonType="button"
              text={activeCard.buttonLabel}
              icon={activeCard.buttonIcon}
              onClick={handleDownloadPdf}
              disabled={isGeneratingPdf}
              styleOption={`cmp-button__secondary ${styles['cmp-download-pdf__button']}`}
            />
          </div>
        </div>
      </div>
    )
  );
}
